import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth.context";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  HelpCircle,
  MessageCircle,
  Plus,
  ChevronRight,
  ChevronDown,
  Loader2,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  Search,
  Eye,
  Reply,
  Paperclip,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { format, formatDistanceToNow } from "date-fns";

const CATEGORIES = [
  "Contribution",
  "Loan",
  "Payroll Deduction",
  "Penalty",
  "Account",
  "Technical Issue",
  "Other",
] as const;

const STATUS_COLORS: Record<string, string> = {
  Open: "bg-blue-100 text-blue-700 border-blue-200",
  "In Review": "bg-amber-100 text-amber-700 border-amber-200",
  Answered: "bg-green-100 text-green-700 border-green-200",
  Closed: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function SupportCenter() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [viewTicket, setViewTicket] = useState<any>(null);
  const [replyText, setReplyText] = useState("");

  // Form state
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { data: tickets, isLoading } = useQuery(
    ["my-support-tickets", user?.id],
    async () => {
      const { data } = await api.get("/member/support-tickets/my");
      return data.data || [];
    },
    { enabled: !!user?.id }
  );

  const createMutation = useMutation(
    async () => {
      const { data } = await api.post("/member/support-tickets", { subject, category, message });
      return data;
    },
    {
      onSuccess: () => {
        toast.success("Support ticket submitted successfully");
        setShowForm(false);
        setSubject("");
        setCategory("");
        setMessage("");
        queryClient.invalidateQueries(["my-support-tickets"]);
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Failed to submit ticket");
      },
    }
  );

  const replyMutation = useMutation(
    async () => {
      const { data } = await api.post(`/member/support-tickets/${viewTicket.id}/replies`, { message: replyText });
      return data;
    },
    {
      onSuccess: () => {
        toast.success("Reply added");
        setReplyText("");
        queryClient.invalidateQueries(["my-support-tickets"]);
        // Refresh ticket view
        if (viewTicket) {
          api.get(`/member/support-tickets/${viewTicket.id}`).then(({ data }) => setViewTicket(data.data));
        }
      },
    }
  );

  const ticketList = Array.isArray(tickets) ? tickets : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <nav className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
            <a href="/member/dashboard" className="hover:text-[#009A44]">Dashboard</a>
            <ChevronRight className="h-3 w-3" />
            <span className="text-slate-700 font-medium">Support Center</span>
          </nav>
          <h1 className="text-xl font-bold text-slate-900">Support Center</h1>
          <p className="text-sm text-slate-500 mt-0.5">Ask questions and track responses from cooperative leaders</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#009A44] hover:bg-[#00803a] gap-2">
          <Plus className="h-4 w-4" />
          Ask Question
        </Button>
      </div>

      {/* Tickets */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse h-24 bg-white rounded-xl border border-slate-100 p-4" />
          ))}
        </div>
      ) : ticketList.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16 text-slate-400">
            <HelpCircle className="h-12 w-12 text-slate-300 mb-4" />
            <p className="text-sm font-medium text-slate-500">No support tickets yet</p>
            <p className="text-xs text-slate-400 mt-1">Have a question? Click "Ask Question" to get started.</p>
            <Button onClick={() => setShowForm(true)} variant="outline" className="mt-4 gap-2">
              <MessageCircle className="h-4 w-4" />
              Ask Your First Question
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {ticketList.map((ticket: any) => (
            <div
              key={ticket.id}
              className="bg-white rounded-xl border border-slate-200 p-4 cursor-pointer hover:border-slate-300 transition-colors"
              onClick={() => {
                setViewTicket(ticket);
                setReplyText("");
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-semibold text-slate-900">{ticket.subject}</h3>
                    <span className={cn("px-2 py-0.5 text-[10px] font-medium rounded-full border", STATUS_COLORS[ticket.status] || "")}>
                      {ticket.status}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{ticket.message}</p>
                  <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-400">
                    <span>{ticket.category}</span>
                    <span>&middot;</span>
                    <span>{formatDistanceToNow(new Date(ticket.createdAt), { addSuffix: true })}</span>
                    {ticket.replies?.length > 0 && (
                      <>
                        <span>&middot;</span>
                        <span className="flex items-center gap-1">
                          <MessageCircle className="h-3 w-3" />
                          {ticket.replies.length} reply{ticket.replies.length > 1 ? "ies" : ""}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300 shrink-0 mt-1" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Ticket Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Ask a Question</DialogTitle>
            <DialogDescription>Submit a support ticket and a leader will respond shortly.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Subject <span className="text-red-500">*</span></Label>
              <Input
                placeholder="Brief summary of your question"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Category <span className="text-red-500">*</span></Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Message <span className="text-red-500">*</span></Label>
              <Textarea
                placeholder="Describe your issue or question in detail..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!subject || !category || !message || createMutation.isLoading}
              className="bg-[#009A44] hover:bg-[#00803a]"
            >
              {createMutation.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Ticket Dialog */}
      <Dialog open={!!viewTicket} onOpenChange={(o) => { if (!o) setViewTicket(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          {viewTicket && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <DialogTitle className="text-base">{viewTicket.subject}</DialogTitle>
                  <span className={cn("px-2 py-0.5 text-[10px] font-medium rounded-full border", STATUS_COLORS[viewTicket.status] || "")}>
                    {viewTicket.status}
                  </span>
                </div>
                <DialogDescription>
                  {viewTicket.category} &middot; {format(new Date(viewTicket.createdAt), "MMM dd, yyyy HH:mm")}
                </DialogDescription>
              </DialogHeader>

              <Card className="bg-slate-50 border-slate-200">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{viewTicket.message}</p>
                </CardContent>
              </Card>

              {/* Replies */}
              {viewTicket.replies?.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <MessageCircle className="h-4 w-4" />
                    Replies ({viewTicket.replies.length})
                  </h4>
                  {viewTicket.replies
                    .filter((r: any) => !r.isInternalNote)
                    .map((reply: any) => (
                      <div key={reply.id} className="bg-white border border-slate-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-semibold text-slate-700">
                            {reply.senderRole === "member" ? "You" : reply.senderRole}
                          </span>
                          <span className="text-[10px] text-slate-400">
                            {formatDistanceToNow(new Date(reply.createdAt), { addSuffix: true })}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 whitespace-pre-wrap">{reply.message}</p>
                      </div>
                    ))}
                </div>
              )}

              {/* Add Reply */}
              {viewTicket.status !== "Closed" && (
                <div className="space-y-2 pt-2">
                  <Label>Add Reply</Label>
                  <Textarea
                    placeholder="Type your reply..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={3}
                  />
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      onClick={() => replyMutation.mutate()}
                      disabled={!replyText.trim() || replyMutation.isLoading}
                      className="bg-[#009A44] hover:bg-[#00803a] gap-2"
                    >
                      {replyMutation.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Reply className="h-4 w-4" />}
                      Send Reply
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
